import styled, { css } from "styled-components";

import {
  DATA_E2E_KEY,
  getWaypointCategoryVisual,
  IStyledAttributes,
  WaypointCategoryEnum,
} from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greenMui,
  greyMui,
  MOBILE_BREAKPOINT,
  orangeMui,
  redMui,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

import {
  RIDE_STATION_BOX_LEFT_WIDTH_PX,
  RIDE_STATION_BOX_RIGHT_WIDTH_PX,
  RIDE_STATION_ROW_GRID_GAP,
  RIDE_STATION_ROW_GRID_TEMPLATE,
} from "./ride-stations-panel.constants";

export const RideStationsPanelStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-station-panel",
})<{
  $isDragging?: boolean;
}>`
  position: relative;
  z-index: ${ScreenDepth.MAIN_CONTAINER};
  background-color: ${rydeColors.white};
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding-bottom: 10px;
  box-sizing: border-box;
  ${({ $isDragging }) =>
    $isDragging
      ? css`
          user-select: none;
          pointer-events: none;
        `
      : css`
          overflow-x: hidden;
          overflow-y: auto;
        `};
`;

export const RideStationsHeaderStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-station-header",
})<{ $isDragging?: boolean }>`
  display: grid;
  grid-template-columns: ${RIDE_STATION_ROW_GRID_TEMPLATE};
  column-gap: ${RIDE_STATION_ROW_GRID_GAP};
  align-items: center;
  height: 32px;
  font-size: 12px;
  line-height: 16px;
  margin-bottom: 7px;
  border: 1px solid ${greyMui._300};
  background-color: ${greyMui._100};
  border-radius: 4px;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  color: ${({ $isDragging }) => ($isDragging ? redMui._900 : blueGreyMui._800)};
  ${({ $isDragging }) =>
    $isDragging &&
    css`
      user-select: none;
      pointer-events: none;
    `};

  .column_checkbox_h {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
  }

  .column_station_info_h {
    min-width: 0;
    display: flex;
    align-items: center;
    overflow: hidden;
  }

  .column_station_header_spacer {
    flex: 0 0 ${RIDE_STATION_BOX_LEFT_WIDTH_PX}px;
  }

  .column_station_header {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .column_time_header {
    flex: 0 0 ${RIDE_STATION_BOX_RIGHT_WIDTH_PX}px;
    min-width: 0;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .column_area_header {
    min-width: 0;
    padding-inline-start: 15px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .column_duration_header {
    padding-inline-start: 5px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .column_toggle_h {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
    margin-inline-end: 3px;

    svg {
      cursor: pointer;
    }
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    gap: ${RIDE_STATION_ROW_GRID_GAP};

    .column_station_info_h {
      flex: 1 1 0;
      min-width: 0;
    }
  }
`;

export const RideStationStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-station",
})<{
  $isDragging?: boolean;
  $isDragClone?: boolean;
}>`
  padding-top: 4px;
  margin-bottom: 3px;
  border-bottom: 1px solid ${greyMui._300};
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background-color: ${rydeColors.white};
  ${({ $isDragClone }) =>
    $isDragClone &&
    css`
      cursor: pointer;
      user-select: none;
      z-index: ${ScreenDepth.RYDE_STATION_DRAG_CLONE};
      border: 3px solid ${greenMui._500};
      box-shadow: 0 2px 15px rgba(0, 0, 0, 0.25);
    `};

  ${({ $isDragging }) =>
    $isDragging &&
    css`
      cursor: pointer;
      user-select: none;
      pointer-events: none;
      opacity: 0.3;
    `};
`;

export const RideStationRowStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-station-row",
})`
  position: relative;
  z-index: ${ScreenDepth.MAIN_CONTAINER};
  display: grid;
  grid-template-columns: ${RIDE_STATION_ROW_GRID_TEMPLATE};
  column-gap: ${RIDE_STATION_ROW_GRID_GAP};
  align-items: center;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  height: 32px;
  font-size: 12px;
  line-height: 16px;
  color: ${rydeColors.black};
  margin-bottom: 8px;

  .column_grip {
    box-sizing: border-box;
    padding-inline-start: 9px;
    display: flex;
    align-items: center;
    &:hover {
      cursor: pointer;
    }
  }
  .column_checkbox {
    @media (max-width: ${MOBILE_BREAKPOINT}) {
      width: 19px;
    }
  }

  .column_passengers {
    box-sizing: border-box;
    display: flex;
    gap: 5px;
    justify-content: space-between;
    align-items: center;
    padding-inline-end: 6px;
    span {
      font-weight: 700;
    }
  }

  .column_station_info {
    min-width: 0;
    display: flex;
    overflow: hidden;
  }
  .column_area {
    color: ${blueGreyMui._500};
    box-sizing: border-box;
    padding-inline-start: 15px;
    min-width: 0;
    overflow: hidden;
  }

  .column_stop {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .column_duration {
    display: flex;
    align-items: center;
    gap: 4px;
    padding-inline-start: 5px;
    min-width: 0;
    overflow: visible;
  }

  .column_duration_text {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .column_icons {
    margin-inline-end: 3px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    gap: ${RIDE_STATION_ROW_GRID_GAP};

    .column_station_info {
      flex: 1 1 0;
      min-width: 0;
    }
  }
`;

export const RideStationAreaCellInnerStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  .area-default-supplier-tags {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
  }

  .area-default-supplier-marker--skipped {
    filter: grayscale(1);
  }

  .area-default-supplier-marker--lookup {
    opacity: 0.7;
    border: 1px solid ${greyMui._500};
    border-radius: 50%;
    padding: 1px 2px;
    box-sizing: border-box;
  }

  .area-title-tooltip-wrap {
    display: flex;
    min-width: 0;
    flex: 1;
    overflow: hidden;
  }

  .area-title-tooltip-wrap .area-title-truncate-inner {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .area-default-supplier-marker {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    line-height: 1;
    opacity: 0.55;
    color: ${greyMui._600};
    cursor: default;
    user-select: none;
  }
`;

export const StationHeaderRightIconsWrapperStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "station-header-right-icons-wrapper",
  },
)`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;

  .trash.disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

export const RideStationPassengerRowStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "ride-station-passenger-row",
  },
)<{
  $isDragClone?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 32px;
  font-size: 12px;
  line-height: 16px;
  z-index: ${ScreenDepth.MAIN_CONTAINER};
  background-color: ${rydeColors.white};
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;

  .hidden {
    visibility: hidden;
  }

  span.bin {
    width: 20px;
    display: none;
  }
  span.chosen {
    display: inline-block;
  }
  .hover_show:not(.chosen) {
    visibility: hidden;
  }
  &:hover {
    .bin {
      display: inline-block;
      line-height: 1;
      vertical-align: middle;
    }
    .hover_show,
    .chosen {
      display: inline-block;
      visibility: visible;
      line-height: 1;
      vertical-align: middle;
    }
    background-color: ${greyMui._200};
  }
  .column_grip {
    padding-inline-start: 9px;
    width: 54px;
    @media (max-width: ${MOBILE_BREAKPOINT}) {
      width: 24px;
    }
  }

  .column_checkbox {
    color: ${blueGreyMui._500};
    width: 20px;
  }
  .column_station_type {
    color: ${blueGreyMui._500};
    line-height: 0;
    width: 40px;
  }
  .column_family {
    flex: 0 1 125px;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 3px;
    overflow: hidden;

    .passenger-name-tooltip-wrap {
      display: flex;
      min-width: 0;
      flex: 1;
      overflow: hidden;
    }

    .passenger-name-tooltip-wrap > * {
      display: block;
      min-width: 0;
      flex: 1;
      max-width: 100%;
      overflow: hidden;
    }

    .passenger-name-tooltip-wrap .passenger-name-truncate-inner {
      display: block;
      min-width: 0;
      flex: 1;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.pointer {
        cursor: pointer;
      }

      &.pointer .Highlight {
        cursor: pointer;
      }
    }

    .hub-ride-companion-mark {
      flex-shrink: 0;
      font-size: 10px;
      line-height: 1;
      color: ${orangeMui._800};
      cursor: default;
    }

    .ride-passenger-site-mismatch-mark {
      flex-shrink: 0;
      color: ${greyMui._400};
      font-size: 14px;
      font-weight: 500;
      line-height: 1;
      margin-inline-start: 4px;
    }
  }
  .column_pax {
    color: ${blueGreyMui._500};
    width: 9px;
    span {
      vertical-align: middle;
    }
  }
  .column_unit {
    width: 65px;
  }
  .column_gender {
    width: 70px;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .column_shift {
    width: 59px;
  }
  .column_flight {
    width: 80px;
  }
  .column_phone {
    a {
      text-decoration: none;
    }
  }

  .column_bin {
    width: 15px;
    margin-inline-start: auto;
    line-height: 1;
    margin-inline-end: 4px;
  }

  ${({ $isDragClone }) =>
    $isDragClone &&
    css`
      cursor: pointer;
      user-select: none;
      border: 3px solid ${greenMui._500};
      box-shadow: 0 2px 15px rgba(0, 0, 0, 0.25);
    `};
`;

export const PassengerRowFlightNumberCell = styled.span.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "ride-passenger-row-flight-number-cell",
  },
)``;

export const GenderDotStyled = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "passenger-gender-dot",
})<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ $color }) => $color};
`;

export const StationBoxLeftStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-station-box-left",
})`
  border: 1px solid ${blueGreyMui.A200};
  height: 32px;
  width: ${RIDE_STATION_BOX_LEFT_WIDTH_PX}px;
  flex: 0 0 ${RIDE_STATION_BOX_LEFT_WIDTH_PX}px;
  border-inline-end: 1px solid ${blueGreyMui.A200};
  border-radius: 4px 0 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: ${rydeColors.black};
`;

export const StationBoxCenterStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-station-box-center",
})`
  border-top: 1px solid ${blueGreyMui.A200};
  border-bottom: 1px solid ${blueGreyMui.A200};
  height: 32px;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-inline: 8px;
  box-sizing: border-box;
  font-weight: 400;
  font-size: 14px;
  color: ${rydeColors.black};
  overflow: hidden;
  .cell_simple {
    width: 100%;
    min-width: 0;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: start;
  }
`;

export const StationBoxRightStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-station-box-right",
})`
  background: ${rydeColors.blueTimeEditBg};
  border: 1px solid ${blueMui._300};
  border-radius: 0 4px 4px 0;
  height: 32px;
  width: ${RIDE_STATION_BOX_RIGHT_WIDTH_PX}px;
  flex: 0 0 ${RIDE_STATION_BOX_RIGHT_WIDTH_PX}px;
  font-weight: 400;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${rydeColors.black};
`;

export const RideStationPlannedActualTimeStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "ride-station-planned-actual-time",
  },
)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-inline: 18px;
  white-space: nowrap;
  font-size: 13px;
  .planned-time {
    color: ${rydeColors.skyBlue};
  }
  .time-separator {
    color: ${rydeColors.skyBlue};
  }
  .actual-time {
    color: ${rydeColors.black};
    font-weight: 600;
  }
`;

export const StationCommentPopperContent = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-station-comment-popover-content",
})`
  padding-inline-start: 15px;
  padding-inline-end: 15px;
  padding-top: 15px;
`;

export const StationCommentPopoverFooter = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-station-comment-popover-footer",
})`
  padding: 12px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
`;

export const RideStationsBulkActionsWrapperStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "ride-station-bulk-actions",
  },
)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-bottom: 22px;
  border: 1px solid ${greyMui._300};
  border-radius: 4px;
  .bulk_panel_header {
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 44px;
    .bulk_panel_title {
      font-size: 14px;
      font-weight: 700;
      color: ${rydeColors.purpleTitle};
    }
  }
  .bulk_back_button {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    min-height: 44px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: none;
    cursor: pointer;
    color: ${greyMui._600};
    svg {
      width: 18px;
      height: 18px;
    }
    &:hover {
      color: ${blueMui._600};
    }
    &:focus-visible {
      outline: 2px solid ${blueMui._600};
      outline-offset: 1px;
    }
  }
  .bulk_actions_menu {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    .bulk_menu_item {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 44px;
      padding-inline: 8px;
      border: none;
      background: none;
      cursor: pointer;
      font-size: 13px;
      text-align: start;
      color: ${greyMui._700};
      .bulk_menu_item_icon {
        width: 16px;
        height: 16px;
        color: ${blueMui._500};
      }
      &:hover {
        color: ${blueMui._600};
        background-color: ${greyMui._100};
      }
      &:focus-visible {
        outline: 2px solid ${blueMui._600};
        outline-offset: -2px;
      }
    }
  }
  .bulk_actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 32px;
    padding-inline: 10px;

    .row_top_info_bulk {
      height: 35px;
      display: flex;
      align-items: center;
      font-size: 14px;
      .person_icon {
        margin-inline: 7px;
        width: 14px;
        height: 14px;
        color: ${rydeColors.purpleTitle};
      }
      label {
        font-weight: 700;
        color: ${rydeColors.purpleTitle};
      }
      span {
        strong {
          margin-inline-end: 3px;
          font-weight: 700;
          color: ${rydeColors.purpleTitle};
        }
        color: ${greyMui._700};
      }
    }
  }
`;

export const BulkActionFormStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-bulk-action-form",
})`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding-inline-start: 10px;

  .stations_dropdowns_container_bulk {
    display: grid;
    grid-template-columns: 1fr 75px 1fr 75px;
    align-items: center;

    .station_buttons_bulk {
      display: flex;
      align-items: center;
    }

    /* Add / open station buttons stay out of the way until the row is in use (RYDE-8146).
       Only where a real hover exists — on touch there is nothing to hover with. */
    @media (hover: hover) {
      .station_buttons_bulk {
        visibility: hidden;
      }
      &:hover .station_buttons_bulk,
      &:focus-within .station_buttons_bulk {
        visibility: visible;
      }
    }
  }
  .checkbox_container_bulk {
    height: 36px;
    display: grid;
    align-items: center;
    grid-template-columns: 1fr 80px 200px;
    svg {
      width: 12px;
      height: 12px;
      margin-inline-start: 4px;
      margin-inline-end: 10px;
    }
    .cancel_button {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      color: ${greyMui._500};
      font-size: 13px;
      &:hover {
        color: ${blueMui._600};
        font-weight: 500;
      }
    }
    .perform_bulk_action_button {
      cursor: pointer;
      display: flex;
      align-items: center;
      color: ${blueMui._500};
      justify-content: flex-end;
      font-size: 13px;
      &:hover {
        color: ${blueMui._600};
        font-weight: 500;
      }
    }
  }
  .only_existing_bulk_station_switch .MuiFormControlLabel-label {
    font-size: 13px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    .checkbox_container_bulk {
      display: flex;
      flex-direction: column;
      height: auto;
      padding: 12px 8px;
      gap: 8px;
      align-items: flex-start;

      .perform_bulk_action_button,
      .cancel_button,
      .only_existing_bulk_station_switch .MuiFormControlLabel-label {
        font-size: 1rem;
      }

      .MuiFormControlLabel-labelPlacementStart {
        margin-inline: 0;
      }
    }
  }
`;

export const RideStationStopIndicatorStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "ride-station-stop-indicator",
  },
)`
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 20px;
    height: 16px;
    cursor: default;
  }
  .stop-indicator--stopped {
    color: ${rydeColors.green};
  }
  .stop-indicator--passed {
    color: ${rydeColors.orange};
  }
  .stop-indicator--not-stopped {
    color: ${rydeColors.whiteButtonBorder};
  }
`;

export const RideStationWarningContentWrapperStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "ride-station-warning-content-wrapper",
  },
)`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  pointer-events: auto;
  .ride_station--warning {
    display: block;
    color: ${redMui._700};
    font-size: 20px;
    cursor: pointer;
    &:hover {
      color: ${redMui._900};
    }
  }
`;

export const RideStationDurationTooltipContentStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "ride-station-duration-tooltip-content",
  },
)`
  line-height: 1.4;

  div {
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: 600;
  }
`;

export const RouteWaypointRowBadge = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "route-waypoint-row-badge",
})<{ $category?: WaypointCategoryEnum }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px dashed
    ${({ $category }) => getWaypointCategoryVisual($category).borderColor};
  color: ${({ $category }) => getWaypointCategoryVisual($category).color};
  background: ${({ $category }) =>
    getWaypointCategoryVisual($category).backgroundColor};
  font-size: 11px;
  font-weight: 700;
`;

export const BulkMoveFormStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-bulk-move-form",
})`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding-inline-start: 10px;

  .bulk_move_direction {
    color: ${blueMui._600};
    font-weight: 700;
  }

  .bulk_move_fields {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .bulk_move_actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    min-height: 44px;
    .perform_bulk_action_button {
      display: flex;
      align-items: center;
      min-height: 44px;
      padding-inline: 8px;
      border: none;
      background: none;
      cursor: pointer;
      color: ${blueMui._500};
      font-size: 13px;
      svg {
        width: 12px;
        height: 12px;
        margin-inline-start: 4px;
      }
      &:hover {
        color: ${blueMui._600};
        font-weight: 500;
      }
      &:focus-visible {
        outline: 2px solid ${blueMui._600};
        outline-offset: 1px;
      }
    }
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    .bulk_move_actions .perform_bulk_action_button {
      font-size: 16px;
    }
  }
`;
